import React, { FC, useEffect } from 'react'

import { CALENDAR, FORM, MEETING_STEPPER } from 'config'
import moment from 'moment-timezone'
import { EditMeetingModal } from './EditMeetingModal'
import { ampli } from '../../../ampli'
import { getRoleFromPath } from '../../../utils/helper'
import DeleteMeetingConfirmation from './MeetingConfirmation/DeleteMeetingConfirmation'
import {
  UserProfile,
  useRescheduleUserCalendarEventMutation,
} from '__generated__/api-types-and-hooks'
import { UserBasicInfo, extendedCalendarEvents } from 'types'
import ModalBox from 'components/Common/ModalBox'
import MeetingUpdatedModalBox from './MeetingUpdatedModalBox'
import MeetingCancelIcon from 'components/Common/SvgIcons/MeetingCancelIcon'
import { CalendarEventViewPreference } from '__generated__/api-types-and-hooks'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { meetingsSchema } from './validations'
import useFormContainer from 'hooks/useFormContainer'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { getUserCalendarEventsAction } from 'store/actions/calendar'
import { showErrorToast } from 'utils/toastHelpers'

interface ICalendarEventEditModalProps {
  user: UserProfile
  event: extendedCalendarEvents
  timeMin?: string
  timeMax?: string
  closeModal: () => void
  meetingType?: string
  showEditButton: boolean
  setShowEditButton: (param: boolean) => void
  updateMeetingAction: (params) => void
  cancelUserCalendarEventAction: (params) => void
  openMeetingConfirmationModalBoxAction: (input: { error: string; stepType: string }) => void
  stepType: string
  isMeetingDeletion: boolean
  errorMessage: string
  getTackleMeetingEventsAction?: (input: { filter: string; ownerId: string }) => void
  isDisabled: boolean
}

export const CalendarEventEditModal: FC<ICalendarEventEditModalProps> = ({
  user,
  event,
  timeMin,
  timeMax,
  closeModal,
  meetingType,
  showEditButton,
  setShowEditButton,
  updateMeetingAction,
  cancelUserCalendarEventAction,
  openMeetingConfirmationModalBoxAction,
  stepType,
  isMeetingDeletion = false,
  errorMessage = '',
  getTackleMeetingEventsAction,
  isDisabled,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { mutate: rescheduleUserCalendarEvent } = useRescheduleUserCalendarEventMutation()

  const methods = useForm({
    mode: 'all',
    resolver: yupResolver(meetingsSchema),
  })

  const filter = meetingType?.trim()

  // New handler for EditMeetingContent's onSave
  const handleEditMeetingSave = (data: {
    title: string
    startDateTime: Date
    endDateTime: Date
    attendees: string[]
    teamMembers: string[]
    description: string
    location: string
    meetingLink: string
    timezone: string
  }) => {
    const updateCalendarMeetingPayload = {
      title: data.title,
      eventId: event?.eventId,
      tackleId: event.tackleId,
      location: data.location,
      eventType: event?.eventType,
      attendees: data.attendees, // External attendees (emails)
      teamMemberList: data.teamMembers, // Internal team members (user IDs)
      description: data.description,
      videoLinkDescription: data.meetingLink,
    }
    const getCalendarEventPayload = {
      timeMin,
      timeMax,
      calendarName:
        user.calendarEventViewPreference === CalendarEventViewPreference.Internal
          ? CalendarEventViewPreference.Internal
          : '',
    }
    // CAL-2: UpdateUserCalendarEventInput cannot change the schedule, so when the
    // date/time moved we go through rescheduleUserCalendarEvent instead, which
    // carries the same editable fields plus startTime/endTime.
    const scheduleChanged =
      new Date(data.startDateTime).getTime() !== new Date(event?.start as Date).getTime() ||
      new Date(data.endDateTime).getTime() !== new Date(event?.end as Date).getTime()

    if (scheduleChanged) {
      rescheduleUserCalendarEvent(
        {
          input: {
            // NB: rescheduleUserCalendarEvent looks the meeting up via `eventId`
            // (calendarEventsRepository.getById), which is the same record id that
            // cancelUserCalendarEvent calls `tackleId`. Its own `tackleId` field is
            // unused server-side but required by the schema.
            tackleId: event?.tackleId as string,
            eventId: event?.tackleId,
            eventType: event?.eventType,
            title: data.title,
            description: data.description,
            location: data.location,
            attendees: data.attendees,
            videoLinkDescription: data.meetingLink,
            startTime: new Date(data.startDateTime).toISOString(),
            endTime: new Date(data.endDateTime).toISOString(),
          },
        },
        {
          // Only close on success. These callbacks must NOT be onSettled: that
          // fires for failures too, which made a rejected reschedule close the
          // modal with no error and drop the event from the grid, so a failed
          // save was indistinguishable from a successful one.
          //
          // Closing here rather than up-front is still required: react-query v3
          // discards the callbacks passed to mutate() when the component unmounts
          // before the mutation settles, so closing the modal immediately meant
          // this block never ran and the grid stayed on the old slot until the
          // next page load.
          onSuccess: () => {
            closeModal()

            // Refetch the grid directly. Routing this through updateMeetingAction
            // fired a second, redundant updateUserCalendarEvent mutation just to
            // piggyback on its refetch, and that saga returns early — skipping the
            // refetch entirely — whenever the extra mutation comes back empty.
            // That is why a rescheduled meeting only moved after a page reload.
            dispatch(getUserCalendarEventsAction.STARTED(getCalendarEventPayload))
          },
          onError: (error: unknown) => {
            // Keep the modal open so the user's edits survive and they can retry.
            const message = error instanceof Error ? error.message : undefined
            showErrorToast(t('calendar.meetingUpdateFailed'), message)
          },
        }
      )
      return
    }

    updateMeetingAction({
      filter,
      getCalendarEventPayload,
      updateCalendarMeetingPayload,
      ownerId: event?.ownerId,
    })
    // No async callback in this path, so closing immediately is safe.
    closeModal()
  }

  const cancelEventHandler = () => {
    let deleteEventPayload = {
      tackleId: event?.tackleId,
      eventId: event?.eventId,
      eventType: event?.eventType,
    }
    let getEventPayload = {
      calendarName:
        user.calendarEventViewPreference === CalendarEventViewPreference.Internal
          ? CalendarEventViewPreference.Internal
          : '',
      timeMin,
      timeMax,
    }
    let getTackleMeetingPayload = {
      filter: meetingType,
      ownerId: event?.inviteeId,
    }

    cancelUserCalendarEventAction({
      deleteEventPayload,
      getTackleMeetingPayload,
      getEventPayload,
    })
  }

  const cancelMeeting = () => {
    let startTime = moment(event.start as Date, 'hh:mm')
    let endTime = moment(event.end as Date, 'hh:mm')
    const userRole = getRoleFromPath()
    cancelEventHandler()
    ampli.meetingCanceled({
      initiatorRole: userRole,
      initiatorName:
        user?.firstName && user?.lastName ? `${user?.firstName} ${user?.lastName}` : '',
      meetingTitle: event?.title,
      originatorName: event?.organizerName || '',
      duration: endTime.diff(startTime, 'minutes').toString(),
    })
    setShowEditButton(false)
  }

  const deleteMeetingConfirmation = () => {
    openMeetingConfirmationModalBoxAction({
      error: '',
      stepType: MEETING_STEPPER.MEETING_WARNING,
    })
  }

  const participant: UserBasicInfo = {
    firstName:
      user.firstName?.trim().toLowerCase() ===
      event?.organizerDetail?.firstName?.trim().toLowerCase()
        ? event?.participantDetail?.firstName
        : event?.organizerDetail?.firstName,
    lastName:
      user.lastName?.trim().toLowerCase() === event?.organizerDetail?.lastName?.trim().toLowerCase()
        ? event?.participantDetail?.lastName
        : event?.organizerDetail?.lastName,
  }

  const handleCancel = () => {
    openMeetingConfirmationModalBoxAction({
      error: '',
      stepType: '',
    })
  }

  const handleClose = () => {
    handleCancel()
    if (stepType === MEETING_STEPPER.MEETING_CONFIRMATION) {
      closeModal()
    }
  }

  const handleModalClose = () => {
    closeModal()
  }

  const handleConfirmationCloseModal = () => {
    if (filter && getTackleMeetingEventsAction) {
      getTackleMeetingEventsAction({ filter, ownerId: event?.inviteeId || '' })
    }
    closeModal()
  }

  useFormContainer(FORM.MEETING_SCHEDULED, methods)

  useEffect(() => {
    if (stepType === MEETING_STEPPER.MEETING_CONFIRMATION && isMeetingDeletion && !errorMessage) {
      handleConfirmationCloseModal()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepType, isMeetingDeletion, errorMessage])

  return (
    <>
      {stepType === MEETING_STEPPER.MEETING_CONFIRMATION && !isMeetingDeletion && (
        <ModalBox
          height={'xs:h-full sm:h-auto max-h-full'}
          Icon={MeetingCancelIcon}
          margin="sm:mx-5"
          onClose={handleClose}
        >
          <MeetingUpdatedModalBox
            advisor={participant}
            errorMessage={errorMessage}
            isMeetingDeletion={isMeetingDeletion}
            modalHandler={handleConfirmationCloseModal}
            openMeetingConfirmationModalBoxAction={openMeetingConfirmationModalBoxAction}
          />
        </ModalBox>
      )}

      {stepType === MEETING_STEPPER.MEETING_WARNING && (
        <DeleteMeetingConfirmation
          meetingName={event?.title || ''}
          onConfirm={cancelMeeting}
          onCancel={handleCancel}
          isLoading={isDisabled}
        />
      )}

      {stepType !== MEETING_STEPPER.MEETING_WARNING &&
        stepType !== MEETING_STEPPER.MEETING_CONFIRMATION && (
          <EditMeetingModal
            onClose={handleModalClose}
            onEdit={() => setShowEditButton(false)}
            onCancel={deleteMeetingConfirmation}
            title={event?.title}
            editMode={!showEditButton}
            eventType={event.eventType as CALENDAR}
            event={event}
            userTimezone={user.timezone || 'UTC'}
            currentUserId={user.id}
            onSave={handleEditMeetingSave}
            isDisabled={isDisabled}
          />
        )}
    </>
  )
}

CalendarEventEditModal.defaultProps = {
  timeMin: '0',
  timeMax: '0',
  showEditButton: true, // Default to true for read-only mode
  setShowEditButton: () => {},
  meetingType: '',
  openMeetingConfirmationModalBoxAction: () => {},
  getTackleMeetingEventsAction: () => {},

  stepType: '',
}
